<template>
    <card class="stretch-layout" xs-tile sm-tile>
        <div>
			<card-text >
				<description-list>
					<description-list-item :title="$t('label.status')">
						<template #text>
							<chip :color="participationStore.participation.status.color" small
								  :help="participationStore.participation.sub_status && participationStore.participation.sub_status.description ? participationStore.participation.sub_status.description : participationStore.participation.status.description">
								{{ participationStore.participation.status.title }}
							</chip>
						</template>
					</description-list-item>

					<description-list-item v-if="participationStore.participation.time !== null" :title="$t('enum.rank_metric.time.title')"
										   :text="participationStore.parseRankValue(participationStore.participation.time, 'time', true)"/>
					<description-list-item v-if="participationStore.participation.distance !== null" :title="$t('enum.rank_metric.distance.title')"
										   :text="participationStore.parseRankValue(participationStore.participation.distance, 'distance', true)"/>
					<description-list-item v-if="participationStore.participation.rounds" :title="$t('enum.rank_metric.rounds.title')"
										   :text="participationStore.parseRankValue(participationStore.participation.rounds, 'rounds', true)"/>
					<description-list-item v-if="participationStore.participation.altitude !== null" :title="$t('enum.rank_metric.altitude.title')"
										   :text="participationStore.parseRankValue(participationStore.participation.altitude, 'altitude', true)"/>
					<description-list-item v-if="participationStore.participation.points !== null" :title="$t('enum.rank_metric.points.title')"
										   :text="participationStore.parseRankValue(participationStore.participation.points, 'points', true)"/>

					<description-list-item v-if="participationStore.participationPaceLabel" :title="`${$t('label.pace')} Ø`"
										   :text="participationStore.participation.pace ? `${participationStore.participation.pace} ${participationStore.participationPaceLabel}` : '-'"/>

					<description-list-item :title="$t('label.started_at')">
						<template #text>
							<dynamic-date :date="participationStore.participation.started_at" short time/>
						</template>
					</description-list-item>

					<description-list-item :title="$t('label.finished_at')">
						<template #text>
							<dynamic-date :date="participationStore.participation.finished_at" short time/>
						</template>
					</description-list-item>

					<description-list-item v-if="participationStore.participation.result_image" :title="$t('label.screenshot')">
						<template #text>
							<btn :to="participationStore.participation.result_image.url" x-small target="_blank">
								<icon :icon="icons.external_link"/>
								<span>{{ $t('action.open') }}</span>
							</btn>
						</template>
					</description-list-item>

					<description-list-item v-if="participationStore.participation.result_info" :title="$t('label.description')">
						<template #text>
							<user-content :text="participationStore.participation.result_info">
							</user-content>
						</template>
					</description-list-item>

				</description-list>
			</card-text>
			<card-buttons v-if="participationStore.authPermissions.can_update_results">
				<btn @click="participationStore.showUpdateResultsModal()" small :color="!participationStore.participation.started_at ? 'success' : undefined">
					<icon :icon="icons.edit"/>
					<span>{{ participationStore.hasFinished ? $t('action.edit') : $t('action.submit_results') }}</span>
				</btn>
			</card-buttons>
		</div>

		<div v-if="participationStore.participation.participationClass.ranks_enabled" class="mb-16">
			<div class="h-42">
				<tabs v-model="selectedView" class="h-full" persist-border grow>
					<tab :color="eventStore.colorStyle" value="ranks" :text="$t('label.ranks')"/>
					<tab :color="eventStore.colorStyle" value="results" :text="$t('label.detail_rank_values')"/>
				</tabs>
			</div>

			<data-table v-if="selectedView === 'ranks'" :url="$api('events.participations.ranks.index', [eventStore.event.id, participationStore.participation.id])"
						hide-search
						hide-pagination-controls
						simple-paginated
						as-guest
						full-width
						sub-rows-prop="intermediateRanks"
						:headers="resultListTableHeaders">

				<template v-slot:item.list="{item, parent}">
					<nuxt-link class="flex flex-column" :to="$app('events-event-results-group', {params: {event: eventStore.event._key, group: item.list_group_key}, query: {list: item.list_key}, state: {group_ts: item.cache_buster}})" :style="{color: eventStore.colorStyle}">
						<span class="text-sm truncate table-text-line">{{ item.list_name }}</span>
						<span v-if="!parent" class="text-3xs truncate text-gray-400">{{ item.list_group_name }}</span>
					</nuxt-link>
				</template>

				<template v-slot:item.rank="{item, parent}">
					<div class="flex items-center">
						<div class="flex flex-column">
							<chip :color="getColorForRank(item.rank)" :icon="icons.rank" small>{{ item.rank }}. {{ $t('label.rank') }}</chip>
							<span v-if="item.name && !parent" class="text-3xs table-text-line">{{ item.name }}</span>
						</div>
						<tooltip v-if="item.is_confirmed" :text="$t('text.confirmed_results')" with-touch>
							<icon :icon="icons.check" small color="success"/>
						</tooltip>
						<tooltip v-else :text="$t('text.temporary_results')" with-touch>
							<icon :icon="icons.warning" small color="warning"/>
						</tooltip>
					</div>
				</template>

				<template v-slot:item.rank_value="{item}">
					<span v-if="!item.primary_rank_value || !item.primary_rank_value_prop">-</span>
					<div v-else class="flex flex-column">
						<div class="flex flex-column">
							<span class="text-2xs text-gray-400">{{ $t(`enum.rank_metric.${item.primary_rank_value_prop}.title`) }}:</span>
							<span>{{ participationStore.parseRankValue(item.primary_rank_value, item.primary_rank_value_prop) }}</span>
						</div>
						<div v-if="item.secondary_rank_value_prop" class="flex flex-column mt-2">
							<span class="text-2xs text-gray-400">{{ $t(`enum.rank_metric.${item.secondary_rank_value_prop}.title`) }}:</span>
							<span>{{ participationStore.parseRankValue(item.secondary_rank_value, item.secondary_rank_value_prop) }}</span>
						</div>
					</div>
				</template>

			</data-table>
			<data-table v-else-if="selectedView === 'results'" :url="$api('events.participations.track_spots.index', [eventStore.event.id, participationStore.participation.id])"
						:params="trackSpotTableParams"
						hide-search
						as-guest
						full-width
						:headers="trackSpotTableHeaders">

				<template v-slot:item.title="{ item }">
					<nuxt-link :to="{path: $web('events.participations.show.map', [eventStore.event._key, participationStore.participation._key]), query: {spot: item.id}}" :style="{color: eventStore.colorStyle}">
						{{ item.title }}
					</nuxt-link>
				</template>

				<template v-slot:item.tracking_time="{ item }">
					<span v-if="!getRecordOnTrackSpot(item)">-</span>
					<span v-else-if="getRecordOnTrackSpot(item).track_spot_position === 1">{{ $date(getRecordOnTrackSpot(item).tracked_at).format('DD.MM.YYYY HH:mm:ss') }}</span>
					<span v-else>+ {{ getRecordOnTrackSpot(item).tracking_time }}</span>
				</template>

				<template v-slot:item.pace="{ item }">
					<span v-if="!getRecordOnTrackSpot(item)">-</span>
					<span v-else>{{ getRecordOnTrackSpot(item).pace ? `${getRecordOnTrackSpot(item).pace} ${participationStore.participationPaceLabel}` : '-' }}</span>
				</template>

			</data-table>
		</div>

    </card>
</template>

<script setup lang="ts">

import {TrackSpotType} from "@spoferan/spoferan-ts-core";
import {checkIcon, editIcon, warningIcon, externalLinkIcon} from "@spoferan/nuxt-spoferan/icons";
import {useEventStore} from "../../../../../store/event";
import {useParticipationStore} from "../../../../../store/participation";
import {mdiMedalOutline} from "@mdi/js";

const eventStore = useEventStore();
const participationStore = useParticipationStore();
const {t} = useI18n();
const route = useRoute();
const {$date} = useNuxtApp();

const trackSpotTableParams = {type: [TrackSpotType.Result]};
const resultListTableHeaders = [
	{value: 'list', text: t('label.result_list')},
	{value: 'rank', text: t('label.rank')},
	{value: 'rank_value'},
];
const trackSpotTableHeaders = [
	{value: 'title'},
	{
		text: t('label.time'), value: 'tracking_time', min_width: 85, formatter: ({item}) => {
			if (item.position === 1) {
				return item.tracked_at ? $date(item.tracked_at).format('DD.MM.YYYY HH:mm:ss') : '-'
			}
			return item.tracking_time ? `+${item.tracking_time}` : '-'
		}
	},
	participationStore.participationPaceLabel ? {
		text: `Ø ${t('label.pace')}`, value: 'pace', min_width: 100, align: 'center', formatter: ({item}) => {
			return item.pace ? `${item.pace} ${participationStore.participationPaceLabel}` : '-';
		}
	} : null
].filter(Boolean);
const icons = {
	external_link: externalLinkIcon,
	check: checkIcon,
	edit: editIcon,
	warning: warningIcon,
	rank: mdiMedalOutline,
};

const selectedView = ref(route.query.view ?? 'ranks');

const {getColorForRank} = useColorHelpers();

function getRecordOnTrackSpot(trackSpot) {
	if (!trackSpot.trackSpotRecords || !trackSpot.trackSpotRecords.length) {
		return null;
	}
	return trackSpot.trackSpotRecords[0];
}

useMeta({
	title: eventStore.buildPageTitle(t('view.events.participations.show.results.title', {
		event: eventStore.event.display_name,
		user: participationStore.participation.display_name
	})) as string,
	noIndex: true
})

</script>

